import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import { Button } from '@/components/ui/button';

const STALE_BUNDLE_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
  /Importing a module script failed/i,
  /'text\/html' is not a valid JavaScript MIME type/i,
  /Expected a JavaScript.*module script.*MIME type/i,
  /Loading module from .* was blocked because of a disallowed MIME type/i,
];

function isStaleBundleError(error: Error): boolean {
  if (error.name === 'ChunkLoadError') return true;
  return STALE_BUNDLE_PATTERNS.some((pattern) => pattern.test(error.message));
}

function getDetail(error: unknown): string | null {
  if (isRouteErrorResponse(error) && typeof error.data === 'string') {
    return error.data;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return null;
}

function StaleBundleView({ error }: Readonly<{ error: Error }>) {
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    Sentry.captureMessage('Stale frontend bundle detected', {
      level: 'info',
      extra: { name: error.name, message: error.message },
    });
  }, [error]);

  return (
    <main
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center"
    >
      <h1 className="text-2xl font-semibold tracking-tight">A new version was deployed</h1>
      <p className="text-muted-foreground max-w-md">
        Reload the page to pick up the latest changes.
      </p>
      <Button
        disabled={reloading}
        onClick={() => {
          setReloading(true);
          window.location.reload();
        }}
      >
        {reloading ? 'Reloading…' : 'Reload'}
      </Button>
    </main>
  );
}

export default function RootErrorBoundary() {
  const error = useRouteError();

  if (error instanceof Error && isStaleBundleError(error)) {
    return <StaleBundleView error={error} />;
  }

  const heading = isRouteErrorResponse(error)
    ? `${String(error.status)} ${error.statusText}`
    : 'Something went wrong';
  const detail = getDetail(error);

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-8 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">{heading}</h1>
      {detail && <p className="text-muted-foreground">{detail}</p>}
    </main>
  );
}
